"use client";

import { ImageIcon, X } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { ImageViewer } from "@/components/image-viewer";
import { ImageViewerToolbar } from "@/components/image-viewer-toolbar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import type { AsidePanelResize } from "@/hooks/use-aside-view";
import { useIsMobile } from "@/hooks/use-media-query";

export interface ImageViewerPanelProps {
  url: string;
  fileName: string;
  onClose: () => void;
  onOpenInNewWindow?: () => void;
  resize: AsidePanelResize;
}

/** Responsive signed-image viewer: full-screen dialog on mobile, aside on desktop. */
export function ImageViewerPanel({
  url,
  fileName,
  onClose,
  onOpenInNewWindow,
  resize,
}: ImageViewerPanelProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const header = (
    <header className="flex min-h-[53px] shrink-0 items-center justify-between gap-2 border-b px-3 py-2.5">
      <div className="flex min-w-0 items-center gap-2">
        <ImageIcon className="size-4 shrink-0 text-muted-foreground" />
        <span className="truncate text-sm font-semibold" title={fileName}>
          {fileName}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <ImageViewerToolbar
          fileUrl={url}
          fileName={fileName}
          onOpenInNewWindow={onOpenInNewWindow}
        />
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onClose}
          aria-label={t("viewer.closeViewer")}
        >
          <X className="size-4" />
        </Button>
      </div>
    </header>
  );

  if (isMobile) {
    return (
      <Dialog open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent
          className="flex h-full max-h-full w-full max-w-full flex-col gap-0 overflow-hidden rounded-none p-0"
          hideCloseButton
          data-testid="image-viewer-dialog"
        >
          <DialogTitle className="sr-only">{fileName}</DialogTitle>
          {header}
          <ImageViewer url={url} fileName={fileName} className="min-h-0 flex-1" />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <motion.aside
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "spring", damping: 30, stiffness: 300 }}
      className="relative flex h-full min-w-0 shrink-0 flex-col overflow-hidden border-l bg-background"
      style={{ width: resize.width }}
      data-testid="image-viewer-panel"
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t("viewer.dragToResize")}
        onPointerDown={resize.onPointerDown}
        onPointerMove={resize.onPointerMove}
        onPointerUp={resize.onPointerUp}
        onDoubleClick={resize.onDoubleClick}
        className="absolute -left-[3px] top-0 z-20 h-full w-1.5 touch-none cursor-col-resize select-none transition-colors hover:bg-primary/30 active:bg-primary/40"
      />
      {header}
      <ImageViewer url={url} fileName={fileName} className="min-h-0 flex-1" />
    </motion.aside>
  );
}
